/** @format */

import {Checkbox as CheckboxRadix} from '@/components/ui/checkbox';
import handleInputError from './handle_input_error';
import {Checkbox} from '@sttark/design-system';
import handleError from './handle_error';
import React from 'react';

/**
 * Wrap an input with our input error handler
 * Meant to be as flexible and transparent as possible
 * Steps for usage.
 * <InputWithErrorHandler
 *    className="form-control"
 *    type="text"
 *    error={props.error}
 *    errorPath="company"
 *    value={props.company || ''}
 *    onChange={props.onInputChange.bind(this, 'user.company')}
 * />
 * Import this component and give it all the normal values you would for an input but also add
 * error - This is the error object that contains the messages for fields {'user_detail': { 'text_number' : {'_empty':'hey! do not leave me empty'}}}
 * errorPath - This will look for the error at that endpoint. So for user_detail text_number error, you would pass user_detail.text_number
 *
 * If you wish to pass a custom component instead of the checkbox, pass two children. The first child will render when checked, while
 * the second child will render when unchecked. This is useful for rendering custom elements as checkboxes but yer keeping
 * our error handling.
 */
export default handleError(
    handleInputError(props => {
        let {
            className,
            hasError,
            errorMessage,
            onChangeValue,
            newestds,
            variant = 'primary',
            size,
            children,
            ...inputProps
        } = props;
        if (hasError) {
            className = className.split(' ');
            className.push('is-invalid');
            inputProps.className = className.join(' ');
        } else {
            inputProps.className = className;
        }
        const _getErrorFeedback = (hasError, errorMessage) => {
            if (hasError) {
                return (
                    <span style={{fontSize: '.9em', color: 'red', display: 'block'}}>
                        {errorMessage}
                    </span>
                );
            }
            return null;
        };

        // Go ahead and just send the value, not the full event
        const onChange = onChangeValue
            ? ({target: {checked}}) => onChangeValue(checked ? true : false)
            : inputProps.onChange;

        // At times we want to render custom elements as checkbox but yet, we want to use the same error handling.
        // This allows us to pass children and render them as the checkbox
        if (children) {
            // pass the onchange event to the children
            children = React.Children.map(children, child => {
                return React.cloneElement(child, {onClick: onChange});
            });

            if (props.value) {
                return (
                    <div>
                        {children[0]}
                        {hasError && _getErrorFeedback(hasError, errorMessage)}
                    </div>
                );
            }
            return (
                <div>
                    {children[1]}
                    {hasError && _getErrorFeedback(hasError, errorMessage)}
                </div>
            );
        }

        return (
            <>
                {_getErrorFeedback(hasError, errorMessage)}
                {newestds && (
                    <Checkbox
                        checked={props.value ? 'checked' : ''}
                        disabled={props.disabled}
                        onChange={onChange}
                        {...inputProps}
                    />
                )}
                {!newestds && (
                    <CheckboxRadix
                        onCheckedChange={val => onChange({target: {checked: val}})}
                        checked={props.value ? 'checked' : ''}
                        disabled={props.disabled}
                        variant={variant}
                        size={size}
                        {...inputProps}
                    />
                )}
            </>
        );
    })
);
